import {useMemo} from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {motion, useReducedMotion} from 'framer-motion';
import {TablerPlus, TablerX} from '../../icons';
import {
  resolveEncounterModality,
  toJsDate,
} from '../../services/encounterService';
import InfoPill from '../common/InfoPill';
import {
  PLANNER_MOTION_EASE,
  PLANNER_VIEW_ENTRANCE_DURATION_MS,
} from './plannerMotion';

const MotionBox = motion(Box);

// ---------------------------------------------------------------------------
// PlannerContinueSessionBanner
//
// A small card pinned under the planner's tab row. It surfaces the single
// most-recent continuable session so a provider whose paused scribe/interpreter
// auto-ended (e.g. they stepped out to discuss with residents) can resume it in
// one tap instead of digging through Recent.
//
// Visually it sits quietly in the planner's neutral surface vocabulary — a
// plain panel background and border, with only the Continue button carrying the
// opalite accent — so it stays useful without pulling focus from the schedule.
// The Continue button mirrors the opalite "+" New action, and the X dismisses
// it for the session.
//
// The continuation itself is delegated upward to `useContinueEncounter` via
// `onContinue`, so this component stays purely presentational.
// ---------------------------------------------------------------------------

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

function formatEndedAgo(date: Date | null): string {
  if (!date) return '';
  const diff = Date.now() - date.getTime();
  if (diff < MINUTE_MS) return 'Ended just now';
  if (diff < HOUR_MS) {
    const mins = Math.max(1, Math.round(diff / MINUTE_MS));
    return `Ended ${mins} min ago`;
  }
  const hours = Math.round(diff / HOUR_MS);
  return `Ended ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
}

const PlannerContinueSessionBanner = ({
  encounter,
  onContinue,
  isContinuing = false,
  onDismiss,
  styles,
}: Record<string, any>) => {
  const {modalityLabel, language, patientName, endedAgo} = useMemo(() => {
    const wf =
      encounter.workflow_type || encounter.workflowType || 'medical_scribing';
    const {badge} = resolveEncounterModality(wf);
    const endRef = encounter.end_time || encounter.start_time;
    const endDate = endRef instanceof Date ? endRef : toJsDate(endRef);
    return {
      modalityLabel: badge,
      language: encounter.foreign_language || encounter.foreignLanguage || '',
      patientName: (encounter.patient_name || '').trim() || 'Unlinked session',
      endedAgo: formatEndedAgo(endDate),
    };
  }, [encounter]);

  const prefersReducedMotion = useReducedMotion();
  const motionTransition = {
    duration: prefersReducedMotion
      ? 0
      : PLANNER_VIEW_ENTRANCE_DURATION_MS / 1000,
    ease: PLANNER_MOTION_EASE,
  };
  // Collapse height and cancel the parent column's gap (`-0.75rem`) so the rows
  // below glide up to fill the space instead of leaving a hole on dismiss.
  const exitMotion = prefersReducedMotion
    ? {opacity: 0}
    : {opacity: 0, height: 0, marginBottom: '-0.75rem'};

  return (
    <MotionBox
      data-testid="planner-continue-session-banner"
      position="relative"
      flex="0 0 auto"
      bg={styles.surfaceBg}
      borderWidth="1px"
      borderColor={styles.borderColor}
      borderRadius="lg"
      overflow="hidden"
      px={4}
      py={3}
      initial={false}
      animate={{opacity: 1, height: 'auto'}}
      exit={exitMotion}
      transition={motionTransition}
    >
      <Flex align="center" justify="space-between" gap={3} minW={0}>
        <Box minW={0} flex="1 1 auto">
          <Text
            fontSize="xs"
            fontWeight="medium"
            color={styles.metaTextColor}
            textTransform="uppercase"
            letterSpacing="0.04em"
            lineHeight="1.2"
            whiteSpace="nowrap"
          >
            Pick up where you left off
          </Text>
          <Text
            fontSize="md"
            fontWeight="semibold"
            color={styles.textColor}
            lineHeight="1.25"
            mt={1}
            noOfLines={1}
          >
            {patientName}
          </Text>
          <HStack spacing={2} mt={1.5} minW={0} flexWrap="wrap">
            <InfoPill borderRadius="lg">{modalityLabel}</InfoPill>
            {language && (
              <InfoPill iconKey="language" borderRadius="lg" translate="no">
                {language}
              </InfoPill>
            )}
            {endedAgo && (
              <Text
                fontSize="sm"
                color={styles.metaTextColor}
                lineHeight="1.4"
                whiteSpace="nowrap"
              >
                {endedAgo}
              </Text>
            )}
          </HStack>
        </Box>
        <HStack spacing={1} flexShrink={0}>
          <Button
            type="button"
            size="sm"
            leftIcon={<TablerPlus size={14} aria-hidden="true" />}
            minH={{base: '44px', md: '36px'}}
            px={3}
            borderRadius="lg"
            bg="opalite.600"
            color="white"
            fontSize="sm"
            fontWeight="medium"
            lineHeight="1.25"
            isLoading={isContinuing}
            loadingText="Opening\u2026"
            onClick={() => onContinue?.(encounter)}
            aria-label={`Continue ${patientName} session`}
            _hover={{bg: 'opalite.700', color: 'white'}}
            _active={{bg: 'opalite.700'}}
            _focusVisible={styles.focusOutlineStyles}
          >
            Continue
          </Button>
          <Tooltip label="Dismiss" hasArrow openDelay={300}>
            <IconButton
              type="button"
              aria-label="Dismiss continue session"
              icon={<TablerX size={16} aria-hidden="true" />}
              size="sm"
              variant="ghost"
              borderRadius="lg"
              minW={{base: '44px', md: '36px'}}
              minH={{base: '44px', md: '36px'}}
              color={styles.metaTextColor}
              onClick={() => onDismiss?.(encounter)}
              _hover={{bg: styles.rowHoverBg}}
              _active={{bg: styles.rowHoverBg}}
              _focusVisible={styles.focusOutlineStyles}
            />
          </Tooltip>
        </HStack>
      </Flex>
    </MotionBox>
  );
};

export default PlannerContinueSessionBanner;
